import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, User, Code, Loader2, Clock, Zap, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DataTable from '../components/DataTable';

const LaborEstimator = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const messagesEndRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Sample labor hour questions
  const defaultQuestions = [
    "What are the labor hours for Carbon Steel pipe, 10 inch, Standard weight?",
    "Show me labor hours for HDPE pipe, 18 inch, DR 17",
    "Find labor hours for 12 inch Carbon Steel pipe with Butt Weld connection",
    "Show me labor hours for different pipe sizes in Carbon Steel material",
    "Compare labor hours between Standard and Extra Strong schedules"
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll to bottom if there are messages, otherwise stay at top
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Timer and progress bar effect
  useEffect(() => {
    if (loading) {
      // Reset timer and progress when loading starts
      setElapsedTime(0);
      setProgress(0);
      
      const MAX_TIME = 40; // 40 seconds
      const startTime = Date.now();
      
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const progressPercent = Math.min((elapsed / MAX_TIME) * 100, 100);
        
        setElapsedTime(elapsed);
        setProgress(progressPercent);
      }, 100); // Update every 100ms for smooth progress
    } else {
      // Clear timer when loading stops
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setElapsedTime(0);
      setProgress(0);
    }

    // Cleanup on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [loading]);

  // Format markdown for rich text display
  const formatMarkdown = (text) => {
    if (!text) return '';
    
    let formatted = text;
    
    // Headers
    formatted = formatted
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-gray-900 mb-2 mt-4">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold text-gray-900 mb-3 mt-4">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-gray-900 mb-4">$1</h1>');
    
    // Code blocks (do before other replacements)
    formatted = formatted
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-2 rounded text-sm font-mono overflow-x-auto my-2">$1</pre>')
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-sm font-mono">$1</code>');
    
    // Numbered lists - wrap consecutive items
    formatted = formatted.replace(/(\d+\.\s+[^\n]+(?:\n\d+\.\s+[^\n]+)*)/g, (match) => {
      const items = match.split(/\n/).filter(item => item.trim());
      return '<ol class="list-decimal list-inside mb-3 space-y-1 ml-4">' + 
        items.map(item => `<li class="mb-1">${item.replace(/^\d+\.\s+/, '')}</li>`).join('') + 
        '</ol>';
    });
    
    // Bullet lists - wrap consecutive items
    formatted = formatted.replace(/(^[-*]\s+[^\n]+(?:\n[-*]\s+[^\n]+)*)/gm, (match) => {
      const items = match.split(/\n/).filter(item => item.trim());
      return '<ul class="list-disc list-inside mb-3 space-y-1 ml-4">' + 
        items.map(item => `<li class="mb-1">${item.replace(/^[-*]\s+/, '')}</li>`).join('') + 
        '</ul>';
    });
    
    // Bold and italic (after lists to avoid conflicts)
    formatted = formatted
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
    
    // Line breaks and paragraphs
    formatted = formatted
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br>')
      .replace(/^(.*)$/gm, '<p class="mb-2">$1</p>')
      .replace(/<p class="mb-2"><\/p>/g, '')
      .replace(/<p class="mb-2"><br><\/p>/g, '')
      .replace(/<p class="mb-2">(<[hou])/g, '$1') // Remove p tags around headers/ul/ol
      .replace(/(<\/[hou]>)<\/p>/g, '$1');
    
    return formatted;
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSuggestedQuestions([]); // Clear previous suggestions
    setLoading(true);

    try {
      // Prepare conversation history
      const conversationHistory = messages.slice(-6).map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

      const response = await axios.post('/api/labor-estimator/message', {
        message: messageText,
        conversationHistory: conversationHistory
      });

      if (response.data.success) {
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: response.data.explanation || response.data.message,
          data: response.data.data,
          sqlQuery: response.data.sqlQuery,
          columns: response.data.columns,
          rowCount: response.data.rowCount,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, botMessage]);
        toast.success('Query executed successfully!');

        // Generate suggested questions
        try {
          const suggestionsResponse = await axios.post('/api/labor-estimator/suggestions', {
            userMessage: messageText,
            responseData: {
              data: response.data.data,
              rowCount: response.data.rowCount,
              columns: response.data.columns
            },
            conversationHistory: conversationHistory
          });

          if (suggestionsResponse.data.success && suggestionsResponse.data.suggestions) {
            setSuggestedQuestions(suggestionsResponse.data.suggestions);
          }
        } catch (suggestionsError) {
          console.error('Error generating suggestions:', suggestionsError);
          // Don't show error to user, just continue without suggestions
        }
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: response.data.message || 'Sorry, I couldn\'t process your request.',
          error: true,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, errorMessage]);
        toast.error('Failed to process query');
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        error: true,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
      toast.error('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex-shrink-0">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Labor-Hour Estimator</h1>
          </div>
          <p className="text-sm text-gray-600 ml-0 sm:ml-14">
            Trained on public construction data
          </p>
        </div>
        
        <div className="flex-1 flex flex-col gap-4">

        {/* Chat Interface */}
        <div className="w-full flex-1">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 h-[500px] sm:h-[600px] md:h-[700px] flex flex-col">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-8">
              
              {messages.length === 0 && (
                <div className="text-center py-8">
                  {/* Default Questions */}
                  <div className="max-w-lg mx-auto">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-6">
                      Sample Labor Hour Questions
                    </h2>
                    <div className="grid grid-cols-1 gap-2">
                      {defaultQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => sendMessage(question)}
                          className="text-left p-3 border border-gray-200 rounded-lg text-sm transition-colors bg-gray-50 hover:bg-orange-50 hover:border-orange-300 text-gray-700"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`flex items-start space-x-3 max-w-3xl ${
                      message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        message.type === 'user'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {message.type === 'user' ? (
                        <User className="w-5 h-5" />
                      ) : (
                        <Bot className="w-5 h-5" />
                      )}
                    </div>
                    <div
                      className={`flex-1 ${
                        message.type === 'user' ? 'text-right' : 'text-left'
                      }`}
                    >
                      <div
                        className={`inline-block p-4 rounded-lg ${
                          message.type === 'user'
                            ? 'bg-orange-500 text-white'
                            : message.error
                            ? 'bg-red-50 text-red-800 border border-red-200'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {message.type === 'user' ? (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        ) : (
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }}
                          />
                        )}
                      </div>
                      
                      {/* SQL Query Display */}
                      {message.sqlQuery && (
                        <div className="mt-2">
                          <details className="bg-gray-50 rounded-lg p-2">
                            <summary className="cursor-pointer text-xs text-gray-600 flex items-center space-x-1">
                              <Code className="w-3 h-3" />
                              <span>View SQL Query</span>
                            </summary>
                            <pre className="mt-2 text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                              {message.sqlQuery}
                            </pre>
                          </details>
                        </div>
                      )}
                      
                      {/* Data Table */}
                      {message.data && message.data.length > 0 && (
                        <div className="mt-4">
                          <DataTable
                            data={message.data}
                            columns={message.columns}
                            title={`Results (${message.rowCount} rows)`}
                          />
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-start space-x-3 max-w-3xl">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center">
                      <Bot className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-gray-100 text-gray-800 p-4 rounded-lg inline-block min-w-[300px]">
                        <div className="flex items-center space-x-2 mb-3">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Analyzing labor estimation data...</span>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className="bg-orange-500 h-2 rounded-full transition-all duration-100 ease-out"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        
                        {/* Timer */}
                        <div className="text-xs text-gray-600 flex items-center justify-between">
                          <span>Processing...</span>
                          <span className="font-mono">{elapsedTime}s / 40s</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Suggested Questions */}
            {suggestedQuestions.length > 0 && (
              <div className="border-t border-gray-200 px-4 pt-3 pb-2">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 self-center mr-2">Suggested:</span>
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSuggestedQuestions([]); // Clear suggestions when one is clicked
                        sendMessage(question);
                      }}
                      className="text-xs px-3 py-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-full hover:bg-orange-100 hover:border-orange-300 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="border-t border-gray-200 p-4">
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about labor hours for pipes, materials, depths, etc..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="w-full sm:w-auto px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Send</span>
                </button>
              </form>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default LaborEstimator;
